<script lang="ts">
// Track submitting dialogs by name to prevent double submission
// Uses a Set so multiple different dialogs can submit independently
const submittingDialogs = new Set<string>()
</script>

<script lang="ts" setup>
/**
 * A form dialog with save and cancel buttons fixed at the bottom.
 * Renders as an action sheet on mobile and a dialog on desktop.
 * Content area is scrollable for long forms.
 */
import { translateError } from '@shared/validation'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import GForm from './GForm.vue'

const {
  name,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  width = 'superwide',
  fullscreen = false,
  hideFooter = false,
  hideCancelButton = false,
  submitDisabled = false,
  pillButtons = false,
  stackedButtons = false,
  model,
  onSubmit,
  onBack,
  showBackButton,
// eslint-disable-next-line no-spaced-func
} = defineProps<{
  name: string
  submitLabel?: string
  cancelLabel?: string
  width?: 'narrow' | 'wide' | 'mediumwide' | 'superwide' | 'ultrawide' | 'unconstrained'
  fullscreen?: boolean
  hideFooter?: boolean
  hideCancelButton?: boolean
  submitDisabled?: boolean
  pillButtons?: boolean
  // Keep the mobile stacked footer on desktop too, instead of the side-by-side row.
  stackedButtons?: boolean
  model?: Record<string, any>
  onSubmit: (() => any | Promise<any>)
  // Forwarded to GSheetDialog's header back arrow, for a multi-step flow that
  // steps back rather than closing. GSheetDialog only shows it on the action
  // sheet unless showBackButton forces it on.
  onBack?: ((closeModal?: () => void) => void)
  showBackButton?: boolean
}>()

let open = $(defineModel<boolean>('open', { default: false }))

const emit = defineEmits<{
  submit: []
  cancel: []
  close: []
}>()

let submitted = $ref(false)
let submitting = $ref(false)
let errorMessage = $ref<string | null>(null)

// Reset submitted state when dialog opens
watch($$(open), (isOpen) => {
  if (isOpen) {
    submitted = false
    submitting = false
    errorMessage = null
  }
})

function cancel () {
  open = false
  emit('cancel')
}

function close () {
  open = false
  emit('close')
}

async function submit () {
  // Guard against double-click race condition
  // Check both instance-level and dialog-specific flags
  if (submittingDialogs.has(name) || submitting) {
    return
  }

  submittingDialogs.add(name)
  submitting = true
  errorMessage = null

  try {
    const result = await Promise.resolve(onSubmit())
    // A handler returning false keeps the dialog open and owns what happens
    // next (a multi-step flow advancing a step); anything else closes as usual.
    if (result === false) return

    open = false
    submitted = true
    emit('submit')
  }
  catch (e: any) {
    // Use the same error parsing logic as showError
    const messages = translateError(e, { useFullMessages: true })
    errorMessage = messages[0] || 'An unexpected error occurred. Please try again.'

    // Keep modal open and show error within modal instead of toast
  }
  finally {
    submittingDialogs.delete(name)
    submitting = false
  }
}
</script>

<template>
  <GSheetDialog
    v-model:open="open"
    :name="name"
    :width="width"
    :fullscreen="fullscreen"
    :onBack="onBack"
    :showBackButton="showBackButton"
    :as="GForm"
    :asProps="{ onSubmit: submit, model }"
    @close="close"
  >
    <template v-if="!hideFooter" #footer>
      <div class="flex flex-col-reverse gap-4" :class="!stackedButtons && 'lg:flex-row'">
        <GCancelButton
          v-if="!hideCancelButton"
          :name="`cancel-form-modal-button-${name}`"
          :label="cancelLabel"
          :pill="pillButtons"
          type="button"
          @click="cancel"
        />
        <GSaveButton
          :name="`submit-form-modal-button-${name}`"
          :label="submitLabel"
          :pill="pillButtons"
          :disabled="submitDisabled || submitted || submitting"
        />
      </div>

      <div v-if="errorMessage">
        <div
          class="rounded-lg shadow-v2lg p-4 flex items-center w-full mt-4"
        >
          <GIcon :icon="faExclamationCircle" class="mr-4 text-red-base" size="md"/>
          <span class="grow whitespace-pre-wrap font-medium">{{ errorMessage }}</span>
        </div>
      </div>

    </template>

    <template v-if="$slots.header" #header>
      <slot name="header"/>
    </template>

    <slot/>

    <!-- Error display within modal -->

  </GSheetDialog>
</template>
